import React from 'react';
import { PureComponent } from 'react';

import ReactDOM from 'react-dom';
import { getSharedRootStore, hasFeatureFlag } from '@bringg-frontend/global-stores';

interface Props {
	element: JSX.Element;
	nodeId: string;
}

interface State {
	node: null | HTMLElement;
}

const POLL_MAX_RETRIES_COUNT = 40;
const POLL_TIMEOUT = 200;
const NODE_RECHECK_DELAY_MS = 200;

/**
 * Gates node reuse: while OFF the portal re-resolves its node on every update (legacy behaviour),
 * unmounting and remounting the portaled subtree. Consumers that tie teardown to unmount must gate
 * that teardown on the SAME flag - with the flag off the unmount fires on every route change.
 */
export const PORTAL_NODE_REUSE_FF = 'enable_portal_node_reuse';

const isNodeReuseEnabled = () => {
	try {
		return hasFeatureFlag(getSharedRootStore().data.usersStore.currentUser, PORTAL_NODE_REUSE_FF);
	} catch {
		// the shared root store is not wired yet (early bootstrap, isolated tests). Every portal in
		// the app goes through here, so fall back to the legacy behaviour instead of letting the
		// throw escape
		return false;
	}
};

const isInsideTests = process.env.NODE_ENV === 'test';

/**
 * Portal adds props to the rendered element when those are passed to the "params" property of the portal container.
 * Check getComponentInstance method for more details.
 * enable_portal_node_reuse can possibly prevent the Portal from receiving updated props from AJS if the props are
 * updated without the actual portal node disconnect
 */
export class Portal extends PureComponent<Props, State> {
	state: State = {
		node: null
	};
	pollForElementRefTimer: number | null = null;
	nodeRecheckTimer: number | null = null;

	componentDidMount() {
		this.updateNode();
	}

	componentDidUpdate(_: Readonly<Props>, prevState: Readonly<State>) {
		if (!isNodeReuseEnabled()) {
			// legacy behaviour: the node saved in state is assumed disconnected after any update,
			// so the pointer is reset and re-resolved - remounting the whole portaled subtree
			if (prevState.node) {
				this.resetNode();
			}

			return;
		}

		const { node } = this.state;

		if (!node) {
			return;
		}

		// the node is only stale when the DOM that owns it was re-rendered outside React
		// (e.g. ngRoute re-created the template on a route change) - re-resolve it only then;
		// resetting on every update would unmount the whole portaled subtree
		if (!node.isConnected) {
			this.resetNode();
			return;
		}

		// the external teardown can also land right after this update (React and AngularJS both
		// react to the same navigation, in either order), so verify again once the transition settles
		this.scheduleNodeRecheck();
	}

	resetNode = () => {
		this.setState({ node: null }, () => {
			this.updateNode();
		});
	};

	scheduleNodeRecheck = () => {
		if (this.nodeRecheckTimer) {
			clearTimeout(this.nodeRecheckTimer);
		}

		this.nodeRecheckTimer = window.setTimeout(() => {
			this.nodeRecheckTimer = null;

			if (this.state.node && !this.state.node.isConnected) {
				this.resetNode();
			}
		}, NODE_RECHECK_DELAY_MS);
	};

	updateNode = () => {
		if (this.state.node && this.state.node.isConnected) {
			return;
		}

		if (isInsideTests) {
			this.setState({ node: document.getElementById(this.props.nodeId) || document.body });
			return;
		}

		this.pollForElementRef(0);
	};

	pollForElementRef = (retries: number) => {
		this.pollForElementRefTimer = window.setTimeout(() => {
			const node = document.getElementById(this.props.nodeId);

			if (node || retries === POLL_MAX_RETRIES_COUNT) {
				if (!node) {
					console.warn(`unable to find node item for ${this.props.nodeId}`);
				}
				this.setState({ node });
				return;
			}

			this.pollForElementRef(retries + 1);
		}, POLL_TIMEOUT);
	};

	componentWillUnmount = () => {
		if (this.pollForElementRefTimer) {
			clearTimeout(this.pollForElementRefTimer);
			this.pollForElementRefTimer = null;
		}

		if (this.nodeRecheckTimer) {
			clearTimeout(this.nodeRecheckTimer);
			this.nodeRecheckTimer = null;
		}
	};

	// This doesn't seem fine, we might have been forced to do so when legacy Dispatch / Planing was a standard
	// to pass the helper data to the React app.
	// Reconsider the need to pass parameters from AJS to React portals.
	getComponentInstance(node: HTMLElement | null) {
		const paramsAttribute = this.state.node?.getAttribute('params');

		if (!paramsAttribute) {
			return this.props.element;
		}

		const callerScope = (window as any).angular.element(node).scope();
		const callerParams = callerScope[paramsAttribute];

		return React.cloneElement(this.props.element, callerParams);
	}

	render() {
		const node = this.state.node;

		if (node && node.isConnected) {
			return ReactDOM.createPortal(this.getComponentInstance(node), this.state.node!);
		}

		return null;
	}
}
